import React, { Dispatch, SetStateAction, useEffect } from 'react';

import { IItem } from '@rbi-ctg/menu';
import { ProductCustomizationModifiers } from 'components/product-detail-v2/product-customization/product-customization-modifiers';
import { SelectModifierFn } from 'components/product-detail-v2/product-customization/types';
import { getModifiersDefaultOptions } from 'components/product-detail-v2/product-customization/utils';
import { useProductWizardContext } from 'state/product-wizard-v2';
import {
  UserSelectionModifierMap,
  UserSelectionModifierSlotMap,
} from 'state/product-wizard-v2/types';
import { ProductWizardUtils } from 'state/product-wizard-v2/utils';

import { DEFAULT_SLOT_KEY } from './constants';
import { ProductItemFlowContainer } from './styled';

interface IProductItemFlowProps {
  selectedItem: IItem | null;
  modifierSelections: UserSelectionModifierSlotMap;
  setModifierSelections: Dispatch<SetStateAction<UserSelectionModifierSlotMap>>;
}

export const ProductItemFlow: React.FC<IProductItemFlowProps> = ({
  selectedItem,
  modifierSelections,
  setModifierSelections,
}) => {
  const { userSelections, menuObject, submitCustomizations } = useProductWizardContext();

  const [defaultModifiersMap, setDefaultModifiersMap] = React.useState(
    selectedItem ? getModifiersDefaultOptions(selectedItem) : {}
  );

  const pickerAspectExclusions = React.useMemo(() => {
    return ProductWizardUtils.GetPickerAspectExclusions(menuObject);
  }, [menuObject]);

  const handleChangeModifierSelections = React.useCallback<SelectModifierFn>(
    (selections, modifier) => {
      const mappedSelections = selectedItem
        ? Object.values(selections).reduce((acc, selection) => {
            acc[selection._key] = {
              item: selectedItem,
              itemId: selectedItem._id,
              modifier: selection,
              option: modifier,
              comboSlotId: userSelections?.comboSlot?.get(DEFAULT_SLOT_KEY)?.comboSlot?._id,
              isSanityDefaultSelection: false,
            };
            return acc;
          }, {} as UserSelectionModifierMap)
        : {};

      setModifierSelections(current => {
        return {
          ...current,
          [modifier._key]: mappedSelections,
        };
      });
    },
    [selectedItem, setModifierSelections, userSelections?.comboSlot]
  );

  useEffect(() => {
    if (selectedItem) {
      submitCustomizations({
        modifiers: modifierSelections,
        slotKey: DEFAULT_SLOT_KEY,
        selectedItem,
      });
    }
  }, [modifierSelections, selectedItem, submitCustomizations]);

  useEffect(() => {
    const newValue = selectedItem ? getModifiersDefaultOptions(selectedItem) : {};
    setDefaultModifiersMap(newValue);
  }, [selectedItem]);

  if (!selectedItem?.options.length) {
    return null;
  }

  return (
    <ProductItemFlowContainer testID="product-item-flow">
      <ProductCustomizationModifiers
        modifierSelections={modifierSelections}
        modifierDefaults={defaultModifiersMap}
        onModifierSelectionsChange={handleChangeModifierSelections}
        // reset was not requested in design, but leaving for now just in case
        onResetModifierSelections={() => null}
        pickerAspectExclusions={pickerAspectExclusions}
        selectedItem={selectedItem}
      />
    </ProductItemFlowContainer>
  );
};
